import { eachDayOfInterval, endOfDay, format, startOfDay } from 'date-fns';
import type { DateRange } from '@/components/filters/DateRangePicker';
import { supabase } from '@/lib/supabase';
import { useSupabaseQuery } from './useSupabaseQuery';

type SalesDailyRow = {
  day: string;
  revenue_cents: number | string;
  orders: number | string;
};

type TransactionRow = {
  amount_cents: number | string;
  status: string | null;
  meta: Record<string, unknown> | null;
  occurred_at: string;
};

export interface SalesData {
  summary: {
    revenue: number;
    orders: number;
    averageOrderValue: number;
  };
  revenueSeries: Array<{ label: string; value: number }>;
  orderSeries: Array<{ label: string; value: number }>;
  paymentMethodMix: Array<{ method: string; total: number }>;
}

const toNumber = (value: number | string | null | undefined) =>
  typeof value === 'number' ? value : Number(value ?? 0);

const approvedStatuses = new Set(['approved', 'captured', 'settled']);

const fetchSalesData = async (range: DateRange): Promise<SalesData> => {
  if (!supabase) {
    throw new Error('Supabase client is not configured.');
  }

  const start = startOfDay(range.start).toISOString();
  const end = endOfDay(range.end).toISOString();

  const [salesResult, transactionsResult] = await Promise.all([
    supabase
      .from('mv_sales_daily')
      .select('day,revenue_cents,orders')
      .gte('day', start)
      .lte('day', end)
      .order('day', { ascending: true }),
    supabase
      .from('stax_transactions')
      .select('amount_cents,status,meta,occurred_at')
      .gte('occurred_at', start)
      .lte('occurred_at', end)
      .order('occurred_at', { ascending: true })
      .limit(1000),
  ]);

  if (salesResult.error) throw salesResult.error;
  if (transactionsResult.error) throw transactionsResult.error;

  const days = eachDayOfInterval({ start: startOfDay(range.start), end: startOfDay(range.end) });
  const salesByDay = new Map(
    (salesResult.data ?? []).map((row) => [row.day.slice(0, 10), row as SalesDailyRow])
  );

  const revenueSeries = days.map((date) => {
    const key = format(date, 'yyyy-MM-dd');
    const match = salesByDay.get(key);
    const revenue = match ? toNumber(match.revenue_cents) / 100 : 0;
    return { label: format(date, 'MMM d'), value: revenue };
  });

  const orderSeries = days.map((date) => {
    const key = format(date, 'yyyy-MM-dd');
    const match = salesByDay.get(key);
    const orders = match ? toNumber(match.orders) : 0;
    return { label: format(date, 'MMM d'), value: orders };
  });

  const approvedTransactions = (transactionsResult.data ?? [])
    .map((row) => row as TransactionRow)
    .filter((row) => approvedStatuses.has((row.status ?? '').toLowerCase()));

  const totalRevenue = approvedTransactions.reduce(
    (sum, row) => sum + toNumber(row.amount_cents) / 100,
    0
  );
  const totalOrders = approvedTransactions.length;
  const averageOrderValue = totalOrders ? totalRevenue / totalOrders : 0;

  const methodTotals = new Map<string, number>();
  for (const row of approvedTransactions) {
    const meta = row.meta ?? {};
    const brand = (meta.card_brand || meta.payment_method_brand || meta.type || 'Unknown') as string;
    const key = brand || 'Unknown';
    methodTotals.set(key, (methodTotals.get(key) ?? 0) + toNumber(row.amount_cents) / 100);
  }

  const paymentMethodMix = Array.from(methodTotals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([method, total]) => ({ method, total }));

  return {
    summary: {
      revenue: totalRevenue,
      orders: totalOrders,
      averageOrderValue,
    },
    revenueSeries,
    orderSeries,
    paymentMethodMix,
  };
};

export const useSalesData = (range: DateRange) => {
  const key = ['sales', range.start.toISOString(), range.end.toISOString()];
  return useSupabaseQuery(key, () => fetchSalesData(range));
};
